<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select class="input-label col-span-12 md:col-span-2"
                 v-model="selectedValue">
        <el-option key="quickView"
                   :label="$t('views.common.listView.quickView')"
                   value="quickView">
        </el-option>
        <el-option key="allItems" :label="$t('views.common.listView.allItems')"
                   value="allItems">
        </el-option>
        <el-option
            key="export"
            :label="$t('views.common.listView.selectedRows.export')"
            value="export"
        >
        </el-option>
      </el-select>

      <el-input
          :placeholder="$t('views.common.listView.searchRows')"
          class="w-full col-span-12 md:col-span-8"
          :prefix-icon="Search"
          v-model="searchTerm"
      />

      <div class="col-span-12 md:col-span-2">
        <horizontal-button
          :title="t('newQuote')"
          @click.prevent="$router.push($Route.SALES_QUOTES_NEW)"
        />
      </div>
    </div>

    <ag-grid-vue
        v-if="rowModelType"
        class="ag-theme-alpine mt-4 md:mt-0"
        :columnDefs="columnDefs"
        rowSelection="multiple"
        :enable-rtl="$ctx.getDir() === 'rtl'"
        style="height: 85vh"
        :defaultColDef="defaultColDef"
        @cell-double-clicked="onCellDoubleClicked"
        :rowData="rowData"
        :pagination="selectedValue == 'allItems'"
        :paginationAutoPageSize="selectedValue == 'allItems'"
        :onGridReady="onGridReady"
        :rowBuffer="rowBuffer"
        :rowModelType="rowModelType"
        :cacheBlockSize="cacheBlockSize"
        :cacheOverflowSize="cacheOverflowSize"
        :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
        :infiniteInitialRowCount="infiniteInitialRowCount"
        :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, ref } from 'vue'
import { AgGridVue } from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
  ModuleRegistry
} from 'ag-grid-community'
import { useI18n } from 'vue-i18n'
import { formatPostgresDate } from '@/utils/date'
import { formatMoney } from '@/utils/money'
import { Search } from '@element-plus/icons-vue'
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model'
import HorizontalButton from '@/components/HorizontalButton.vue'
import QuoteStatusBadge from '@/views/sales/QuoteStatusBadge.vue'

ModuleRegistry.registerModules([InfiniteRowModelModule])

interface QuoteOrder {
  id: string
  externalID: string
  createdAt: string
  validUntil: string | null
  orderStatus: string
  saleOrderID: string | null
  user: { fullName: string; name: string }
  invoice: {
    paymentType: string
    totalDueAmount: number
    currency: string
    precision: number
    customer: { name: string; phone: string } | null
  }
}

interface QuotesPaginated {
  data: QuoteOrder[]
  total: number
}

export default defineComponent({
  name: 'quotes',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          newQuote: 'New Quote',
          quoteNumber: 'Quote #',
          createdAt: 'Created At',
          validUntil: 'Valid Until',
          paymentTerms: 'Payment Terms',
          customer: 'Customer',
          total: 'Total',
          quotedBy: 'Quoted By',
          direct: 'Direct',
          credit_sale: 'Credit Sale',
          layaway: 'Layaway',
          cash_on_delivery: 'Cash on Delivery',
          status: 'Status'
        },
        ar: {
          newQuote: 'عرض سعر جديد',
          quoteNumber: 'رقم العرض',
          createdAt: 'تاريخ الانشاء',
          validUntil: 'صالح حتى',
          paymentTerms: 'شروط الدفع',
          customer: 'الزبون',
          total: 'المجموع',
          quotedBy: 'بواسطة',
          direct: 'مباشر',
          credit_sale: 'آجل',
          layaway: 'تقسيط',
          cash_on_delivery: 'الدفع عند الاستلام',
          status: 'الحالة'
        }
      }
    })

    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)

    onBeforeMount(() => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10
    })

    return {
      t,
      Search,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache
    }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: QuoteOrder[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,
      searchTerm: '',
      selectedValue: 'quickView',
      gridApi: undefined as GridApi | undefined
    }
  },

  components: {
    AgGridVue,
    HorizontalButton
  },

  beforeMount() {
    this.defaultColDef = {
      sortable: false,
      filter: false,
      resizable: true,
      minWidth: 150
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },
      {
        headerName: this.t('quoteNumber'),
        field: 'externalID',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        minWidth: 200
      },
      {
        headerName: this.t('createdAt'),
        field: 'createdAt',
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const q = params.node?.data as QuoteOrder
          if (!q?.createdAt) return ''
          return formatPostgresDate(String(new Date(q.createdAt)), 'DD/MM/YYYY hh:mm A')
        }
      },
      {
        headerName: this.t('status'),
        field: 'saleOrderID',
        cellRenderer: QuoteStatusBadge
      },
      {
        headerName: this.t('validUntil'),
        field: 'validUntil',
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const q = params.node?.data as QuoteOrder
          if (!q?.validUntil) return '-'
          return formatPostgresDate(String(new Date(q.validUntil)), 'DD/MM/YYYY')
        }
      },
      {
        headerName: this.t('paymentTerms'),
        field: 'invoice.paymentType',
        valueGetter: (params) => {
          const q = params.node?.data as QuoteOrder
          if (!q?.invoice?.paymentType) return ''
          return this.t(q.invoice.paymentType)
        }
      },
      {
        headerName: this.t('customer'),
        valueGetter: (params) => {
          const q = params.node?.data as QuoteOrder
          if (!q?.invoice?.customer) return ''
          return q.invoice.customer.name || ''
        }
      },
      {
        headerName: this.t('total'),
        valueGetter: (params) => {
          const q = params.node?.data as QuoteOrder
          if (!q?.invoice) return ''
          return formatMoney(q.invoice.totalDueAmount, q.invoice.currency)
        }
      },
      {
        headerName: this.t('quotedBy'),
        valueGetter: (params) => {
          const q = params.node?.data as QuoteOrder
          if (!q?.user) return ''
          return q.user.fullName || q.user.name || ''
        }
      }
    ]
  },

  watch: {
    searchTerm() {
      this.filterItems()
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export') {
        return
      }

      if (this.selectedValue === 'export') {
        const selected = this.gridApi?.getSelectedRows()
        if (selected?.length) {
          this.gridApi?.exportDataAsCsv({ onlySelected: true })
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.exportSuccess')
          })
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    }
  },

  methods: {
    async updateRowData() {
      this.gridApi?.showLoadingOverlay()
      try {
        const data = await this.$http.get<QuotesPaginated>('/sales/quotes?startRow=0&endRow=10000')
        this.rowData = data.data
        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error: any) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }
      this.gridApi?.hideOverlay()
    },

    getDatasource() {
      return {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          let url = `/sales/quotes?startRow=${params.startRow}&endRow=${params.endRow}`

          if (Object.keys(params.filterModel).length) {
            url += `&query=${params.filterModel['-'].filter}`
          }

          const res = await this.$http.get<QuotesPaginated>(url)
          this.rowData = res.data
          this.gridApi?.hideOverlay()

          params.successCallback(this.rowData, res.total)
        }
      }
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      if (this.selectedValue == 'quickView') {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
      } else if (this.selectedValue == 'allItems') {
        this.updateRowData()
      }
    },

    filterItems() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({ '-': { filter: this.searchTerm } })
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },

    onCellDoubleClicked(event: any) {
      this.$router.push(
        this.$Route.SALES_QUOTES_QUOTE.replace(':id', event.data.id)
      )
    }
  }
})
</script>

<style></style>
